import React, { useState, useEffect, useRef } from 'react';
import { Link, navigate } from 'gatsby';
import { Helmet } from 'react-helmet';
import styled, { createGlobalStyle } from 'styled-components';

const SUBDOMAINS = [
  { name: 'music', path: '/music/' },
  { name: 'photos', path: '/photos/' },
  { name: 'tech', path: '/tech/' },
];

const SelectorGlobalStyle = createGlobalStyle`
  :root {
    --color-bg: #f3f2f2;
    --color-text: #201e1d;
    --color-accent: rgb(232, 90, 79);
    --color-neutral-300: #d7d3d3;
    --font-heading: 'Archivo', system-ui, sans-serif;
    --font-heading-weight: 800;
    --font-body: 'Archivo', system-ui, sans-serif;
    --space-4: 16px;
    --space-8: 32px;
  }

  *, *::before, *::after { box-sizing: border-box; }

  html,
  body {
    margin: 0;
    height: 100%;
    background: var(--color-bg);
  }

  :focus { outline: none; }
  :focus-visible { outline: 2px solid var(--color-accent); outline-offset: 2px; }
  ::selection { background: color-mix(in srgb, var(--color-accent) 30%, transparent); }
`;

const StyledMain = styled.main`
  position: relative;
  min-height: 100vh;
  display: grid;
  font-family: var(--font-body);
  color: var(--color-text);
  background: var(--color-bg);
`;

const StyledSelector = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  overflow: hidden;
  font-family: var(--font-heading);
  font-weight: var(--font-heading-weight);
  font-size: clamp(32px, 7.5vw, 120px);
  line-height: 1.1;
  letter-spacing: -0.03em;

  .window {
    position: relative;
    height: 1.1em;
    justify-self: end;
    min-width: 0;
  }

  nav {
    position: absolute;
    top: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    transition: transform 420ms cubic-bezier(0.2, 0.7, 0.2, 1);
  }

  a {
    display: block;
    height: 1.1em;
    color: var(--color-neutral-300);
    text-decoration: none;
    transition: color 200ms;

    &[aria-current='true'] {
      color: var(--color-accent);
    }
  }

  .domain {
    white-space: nowrap;
  }

  @media (prefers-reduced-motion: reduce) {
    nav,
    a {
      transition: none;
    }
  }
`;

const StyledHint = styled.p`
  position: absolute;
  left: var(--space-8);
  bottom: var(--space-4);
  margin: 0;
  font-size: 13px;
`;

const IndexPage = () => {
  const [active, setActive] = useState(0);
  const activeRef = useRef(active);
  activeRef.current = active;

  useEffect(() => {
    const n = SUBDOMAINS.length;
    const onKey = e => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive(a => (a + 1) % n);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive(a => (a - 1 + n) % n);
      } else if (e.key === 'Enter' && !(e.target instanceof HTMLAnchorElement)) {
        navigate(SUBDOMAINS[activeRef.current].path);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      <Helmet title="Ionwyn">
        <html lang="en" />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Archivo:wght@400;800&display=swap"
        />
      </Helmet>
      <SelectorGlobalStyle />

      <StyledMain>
        <StyledSelector>
          <div className="window">
            <nav aria-label="Subdomains" style={{ transform: `translateY(${-1.1 * active}em)` }}>
              {SUBDOMAINS.map(({ name, path }, i) => (
                <Link
                  key={name}
                  to={path}
                  aria-current={i === active ? 'true' : undefined}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}>
                  {name}
                </Link>
              ))}
            </nav>
          </div>
          <div className="domain">.ionwyn.com</div>
          <div />
        </StyledSelector>

        <StyledHint>↑ ↓ to choose · Enter to go</StyledHint>
      </StyledMain>
    </>
  );
};

export default IndexPage;
